import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payment = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState('checking'); // checking, success, failed

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (!sessionId) {
      // No session ID means they came here directly - redirect to booking
      navigate('/booking');
      return;
    }

    // Check payment status
    checkPaymentStatus(sessionId);
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    console.log('Checking payment status for session:', sessionId);
    
    // Poll for payment status
    let attempts = 0;
    const maxAttempts = 5;
    
    const poll = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        console.log('Payment status response:', response.data);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          
          // Get full booking details
          const bookingId = response.data.booking_id;
          const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
          
          toast({
            title: "Payment Successful!",
            description: "Your booking is confirmed."
          });
          
          // Redirect to confirmation
          setTimeout(() => {
            navigate('/confirmation', { 
              state: { 
                booking: bookingResponse.data,
                sessionId 
              } 
            });
          }, 2000);
          
          return;
        }
        
        // Still pending, try again
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(() => poll(), 2000);
        } else {
          // Give up after max attempts
          setStatus('failed');
          toast({
            title: "Payment Status Unknown",
            description: "Please check your email for confirmation.",
            variant: "destructive"
          });
        }
        
      } catch (error) {
        console.error('Payment status check error:', error);
        setStatus('failed');
        toast({
          title: "Error",
          description: "Failed to verify payment. Please contact us.",
          variant: "destructive"
        });
      }
    };
    
    poll();
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-16 h-16 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Verifying Payment...</h3>
            <p className="text-gray-600">Please wait while we confirm your payment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2 text-gray-900">Payment Successful!</h3>
            <p className="text-gray-600">Redirecting to confirmation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2 text-gray-900">Payment Issue</h3>
            <p className="text-gray-600 mb-4">We couldn't verify your payment. Please check your email or contact us.</p>
            <p className="text-sm text-gray-500">Phone: (437) 263-6134</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default Payment;
